import React, { useState, useEffect, memo } from 'react'
import styled from 'styled-components'
import { animated, useSpring } from 'react-spring'
import Elements from '@/components/Elements/Elements'
import Employees from '@/components/Employee/Employees'
import { useGlobalStore } from '@/stores/globalStore'
import Layers from '@/components/layout/Sidebar/Layers/Layers'
import Bookings from '@/components/layout/Sidebar/Bookings'
import Reports from '@/components/layout/Sidebar/Reports/Reports'
import Menu from '@/components/layout/Sidebar/Menu/Menu'
import { media } from '@/ui/media'

export type SectionType = 'layers' | 'bookings' | 'elements' | 'employees' | 'reports'

const sectionsObj = {
  layers: <Layers />,
  bookings: <Bookings />,
  elements: <Elements />,
  employees: <Employees />,
  reports: <Reports />,
}

const getComponent = (section: SectionType | null) => {
  if (!section) return null
  return React.cloneElement(sectionsObj[section])
}

const SidebarContainer: React.FC = () => {
  const [section, setSection] = useState<null | SectionType>(null)
  const selector = useGlobalStore((state) => state.selector)

  const [style, api] = useSpring(() => ({
    transform: 'translateX(-100%)',
  }))

  const toggleSection = (targetSection: SectionType | null) =>
    section === targetSection ? null : targetSection

  const handleSectionChange = (targetSection: SectionType | null) => {
    const target = toggleSection(targetSection)

    if (target) {
      api.start({
        to: { transform: 'translateX(0%)' },
        onStart: () => setSection(target),
      })
    } else {
      api.start({
        to: { transform: 'translateX(-100%)' },
        onResolve: () => setSection(null),
      })
    }
  }

  useEffect(() => {
    handleSectionChange(selector)
  }, [selector])

  return (
    <>
      <MenuWrapper>
        <Menu />
      </MenuWrapper>

      <SidebarWrapper style={style}>
        <Container>
          <Content>{getComponent(section)}</Content>
        </Container>
      </SidebarWrapper>
    </>
  )
}

export default memo(SidebarContainer)

const MenuWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 75px;
  bottom: 0;
  width: 104px;
  z-index: 1002;
`

const SidebarWrapper = styled(animated.div)`
  position: fixed;
  left: 104px; /* ширина меню */
  top: 75px;
  bottom: 0;
  display: flex;
  align-items: center;
  transform: translateX(-100%);
  z-index: 1001;

  ${media.lg`
    width: 100%;
    left: 0;
  `}
`

const Container = styled.div`
  height: 100%;
  box-shadow: 4px 0px 4px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  background: ${(props) => props.theme.palette.background.primary};
  color: #2c2c2c;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`

const Content = styled.div`
  padding: 30px 40px;
  min-width: 330px;
  position: relative;
  max-height: 100%;
  height: 100%;

  ${media.lg`
    padding: 10px;
    min-width: auto;
    max-width: 100%;
  `}
`
